import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TenantStatus, CreateInternalTenantRequest, InternalTenantListItem } from '@resolve-ai/shared';
import { tenantsApi } from '../lib/tenants-api';
import { ApiError } from '../lib/api-client';

// Stable reference so hooks depending on "tenants" don't re-run every render
// while the query is still loading (before tenantsQuery.data exists).
const EMPTY_TENANTS: InternalTenantListItem[] = [];

export type TenantStatusFilter = 'all' | TenantStatus;

interface ToastState {
  isOpen: boolean;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

const EMPTY_TOAST: ToastState = { isOpen: false, message: '', variant: 'info' };

export function useTenantsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<TenantStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<ToastState>(EMPTY_TOAST);

  const tenantsQuery = useQuery({
    queryKey: ['tenants', statusFilter],
    queryFn: () => tenantsApi.list(statusFilter === 'all' ? {} : { status: statusFilter }),
  });

  const tenants = tenantsQuery.data?.tenants ?? EMPTY_TENANTS;

  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const q = searchQuery.toLowerCase();
    return tenants.filter(
      (t) => t.name.toLowerCase().includes(q) || t.domain.toLowerCase().includes(q)
    );
  }, [tenants, searchQuery]);

  const counts = useMemo(() => {
    const activeTenants = tenants.filter((t) => t.status === 'active');
    const totalUsers = activeTenants.reduce((sum, t) => sum + t.userCount, 0);
    return {
      active: activeTenants.length,
      totalUsers,
    };
  }, [tenants]);

  const showToast = (message: string, variant: ToastState['variant']) =>
    setToast({ isOpen: true, message, variant });

  const createMutation = useMutation({
    mutationFn: (payload: CreateInternalTenantRequest) => tenantsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setShowCreateModal(false);
      showToast('Tenant created successfully', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to create tenant', 'error');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ tenantId, status }: { tenantId: string; status: TenantStatus }) =>
      tenantsApi.update(tenantId, { status }),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      showToast(status === 'active' ? 'Tenant activated' : 'Tenant deactivated', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update tenant', 'error');
    },
  });

  return {
    query: {
      isLoading: tenantsQuery.isLoading,
      hasError: tenantsQuery.isError,
      tenants: filteredTenants,
      counts,
    },
    filters: { statusFilter, setStatusFilter, searchQuery, setSearchQuery },
    modal: {
      isOpen: showCreateModal,
      open: () => setShowCreateModal(true),
      close: () => setShowCreateModal(false),
    },
    mutations: { create: createMutation, updateStatus: updateStatusMutation },
    toast: { ...toast, close: () => setToast(EMPTY_TOAST) },
  };
}
